import React, { useRef, useEffect, useCallback, useState } from "react";
import RFB from "@countersink/s_novnc";
import { observer } from "mobx-react-lite";
import { runInAction } from "mobx";
import { Skeleton, Button } from "@nextui-org/react";
import { Drawer } from "vaul";
import { motion } from "framer-motion";
import clsx from "clsx";
import noVNCContainerStore from "./store/NoVNCContainerStore";
import { useIsDesktop } from "utils/useIsDesktop";

interface NoVNCContainerProps {
  url: string;
  className?: string;
  style?: React.CSSProperties;
  onBringToFront?: () => void;
  showDrawer?: boolean;
}

const isMobileDevice = () => {
  if (typeof navigator !== "undefined") {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }
  return false;
};

const NoVNCContainer: React.FC<NoVNCContainerProps> = observer(
  ({ url, className, style, onBringToFront, showDrawer = true }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const rfbRef = useRef<RFB | null>(null);
    const isAttemptingConnectionRef = useRef(false);
    const autoAttemptIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const manualAttemptIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const autoReconnectionAttemptsRef = useRef(0);
    const manualReconnectionAttemptsRef = useRef(0);
    const hiddenInputRef = useRef<HTMLInputElement | null>(null);
    const attemptStartTimeRef = useRef<number | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const isDesktop = useIsDesktop();

    const lastTapRef = useRef<number | null>(null);
    const TAP_DELAY = 300;

    const connect = useCallback(() => {
      if (containerRef.current && !isAttemptingConnectionRef.current) {
        isAttemptingConnectionRef.current = true;

        if (rfbRef.current) {
          rfbRef.current.disconnect();
          rfbRef.current = null;
        }

        try {
          const rfb = new RFB(containerRef.current, url, {
            qualityLevel: noVNCContainerStore.qualityLevel,
            compressionLevel: noVNCContainerStore.compressionLevel,
          });

          rfb.addEventListener("clipboard", (event: any) => {
            const serverText = event.detail?.text || event.detail || "";
            if (serverText) {
              navigator.clipboard
                .writeText(serverText)
                .catch((err) => console.error("Error", err));
            }
          });

          const isMobile = isMobileDevice();

          rfb.scaleViewport = true;
          rfb.resizeSession = true;
          rfb.clipViewport = false;
          rfb.dragViewport = false;
          rfb.keyboardEnabled = true;
          rfb.focusOnClick = !isMobile;

          const handleConnect = () => {
            runInAction(() => {
              noVNCContainerStore.setConnected(true);
            });

            noVNCContainerStore.setReconnecting(false);
            noVNCContainerStore.setShowReconnectUI(false);

            isAttemptingConnectionRef.current = false;

            if (autoAttemptIntervalRef.current) {
              clearInterval(autoAttemptIntervalRef.current);
              autoAttemptIntervalRef.current = null;
              autoReconnectionAttemptsRef.current = 0;
            }

            if (manualAttemptIntervalRef.current) {
              clearInterval(manualAttemptIntervalRef.current);
              manualAttemptIntervalRef.current = null;
              manualReconnectionAttemptsRef.current = 0;
            }

            const start = Math.floor(noVNCContainerStore.loadingProgress);
            const end = 100;
            const duration = 1000;
            const startTime = Date.now();

            const animateProgress = () => {
              const elapsed = Date.now() - startTime;
              if (elapsed < duration) {
                const progress = start + ((end - start) * elapsed) / duration;
                runInAction(() => {
                  noVNCContainerStore.setLoadingProgress(progress);
                });
                requestAnimationFrame(animateProgress);
              } else {
                runInAction(() => {
                  noVNCContainerStore.setLoadingProgress(100);
                  noVNCContainerStore.setLoading(false);
                });
              }
            };

            animateProgress();
          };

          const handleDisconnect = () => {
            runInAction(() => {
              noVNCContainerStore.setConnected(false);
            });

            isAttemptingConnectionRef.current = false;
            startReconnectionAttempts();
          };

          const handleError = (e: any) => {
            isAttemptingConnectionRef.current = false;
            noVNCContainerStore.setReconnecting(false);
            noVNCContainerStore.setShowReconnectUI(true);
          };

          rfb.addEventListener("connect", handleConnect);
          rfb.addEventListener("disconnect", handleDisconnect);
          rfb.addEventListener("securityfailure", (e: any) => {
            isAttemptingConnectionRef.current = false;
          });
          rfb.addEventListener("error", handleError);

          rfbRef.current = rfb;

          const canvas = (rfbRef.current as any)._canvas as HTMLCanvasElement;
          if (canvas) {
            canvas.addEventListener("touchend", handleCanvasTouchEnd);
            canvasRef.current = canvas;
          }

          noVNCContainerStore.sendClipboardText = (text: string) => {
            rfb.clipboardPasteFrom(text);
          };
        } catch (error) {
          isAttemptingConnectionRef.current = false;
          noVNCContainerStore.setReconnecting(false);
          noVNCContainerStore.setShowReconnectUI(true);
        }
      }
    }, [url]);

    const startConnectionAttempts = useCallback(() => {
      attemptStartTimeRef.current = Date.now();
      connect();
      autoAttemptIntervalRef.current = setInterval(() => {
        if (
          !noVNCContainerStore.isConnected &&
          !isAttemptingConnectionRef.current
        ) {
          const elapsed =
            Date.now() - (attemptStartTimeRef.current || Date.now());
          if (elapsed >= 30000) {
            if (autoAttemptIntervalRef.current) {
              clearInterval(autoAttemptIntervalRef.current);
              autoAttemptIntervalRef.current = null;
            }
            runInAction(() => {
              noVNCContainerStore.remainingTime = 0;
              noVNCContainerStore.setLoading(false);
            });
          } else {
            connect();
            runInAction(() => {
              if (noVNCContainerStore.loadingProgress < 90) {
                noVNCContainerStore.setLoadingProgress(
                  noVNCContainerStore.loadingProgress + 2
                );
              }
            });
          }
        }
      }, 1000);
    }, [connect]);

    const attemptReconnect = (isManual: boolean) => {
      const attemptsRef = isManual
        ? manualReconnectionAttemptsRef
        : autoReconnectionAttemptsRef;
      attemptsRef.current += 1;
      connect();
    };

    const startReconnectionAttempts = useCallback(
      (isManual = false) => {
        const attemptsRef = isManual
          ? manualReconnectionAttemptsRef
          : autoReconnectionAttemptsRef;
        const intervalRef = isManual
          ? manualAttemptIntervalRef
          : autoAttemptIntervalRef;

        if (intervalRef.current !== null) {
          return;
        }

        attemptsRef.current = 0;

        noVNCContainerStore.setReconnecting(true);

        attemptReconnect(isManual);

        intervalRef.current = setInterval(() => {
          if (
            !noVNCContainerStore.isConnected &&
            !isAttemptingConnectionRef.current
          ) {
            if (attemptsRef.current < 2) {
              attemptReconnect(isManual);
            } else {
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
              noVNCContainerStore.setReconnecting(false);
              noVNCContainerStore.setShowReconnectUI(true);
            }
          }
        }, 500);
      },
      [attemptReconnect]
    );

    useEffect(() => {
      if (rfbRef.current) {
        const rfb = rfbRef.current as any;
        rfb._qualityLevel = noVNCContainerStore.qualityLevel;
        rfb._compressionLevel = noVNCContainerStore.compressionLevel;
        rfb._sendEncodings();
      }
    }, [
      noVNCContainerStore.qualityLevel,
      noVNCContainerStore.compressionLevel,
    ]);

    useEffect(() => {
      startConnectionAttempts();
      return () => {
        if (rfbRef.current) {
          if (canvasRef.current) {
            canvasRef.current.removeEventListener(
              "touchend",
              handleCanvasTouchEnd
            );
          }
          rfbRef.current.disconnect();
          rfbRef.current = null;
        }
        if (autoAttemptIntervalRef.current) {
          clearInterval(autoAttemptIntervalRef.current);
          autoAttemptIntervalRef.current = null;
        }
        if (manualAttemptIntervalRef.current) {
          clearInterval(manualAttemptIntervalRef.current);
          manualAttemptIntervalRef.current = null;
        }
      };
    }, [url, startConnectionAttempts]);

    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          if (
            !noVNCContainerStore.isConnected &&
            !isAttemptingConnectionRef.current &&
            autoAttemptIntervalRef.current === null
          ) {
            if (!noVNCContainerStore.showReconnectUI) {
              startReconnectionAttempts();
            }
          }
        } else {
          if (rfbRef.current) {
            rfbRef.current.disconnect();
            rfbRef.current = null;
          }
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }, [noVNCContainerStore.showReconnectUI, startReconnectionAttempts]);

    const handleReconnect = () => {
      if (
        noVNCContainerStore.remainingTime !== null &&
        noVNCContainerStore.remainingTime > 0
      ) {
        if (manualAttemptIntervalRef.current) {
          clearInterval(manualAttemptIntervalRef.current);
          manualAttemptIntervalRef.current = null;
        }
        noVNCContainerStore.setShowReconnectUI(false);
        startReconnectionAttempts(true);
      }
    };

    const keySymMap: { [key: string]: number } = {
      Backspace: 0xff08,
      Tab: 0xff09,
      Enter: 0xff0d,
      Shift: 0xffe1,
      Control: 0xffe3,
      Alt: 0xffe9,
      Pause: 0xff13,
      CapsLock: 0xffe5,
      Escape: 0xff1b,
      PageUp: 0xff55,
      PageDown: 0xff56,
      End: 0xff57,
      Home: 0xff50,
      ArrowLeft: 0xff51,
      ArrowUp: 0xff52,
      ArrowRight: 0xff53,
      ArrowDown: 0xff54,
      Insert: 0xff63,
      Delete: 0xffff,
    };

    const handleCanvasTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const now = Date.now();
      if (lastTapRef.current && now - lastTapRef.current < TAP_DELAY) {
        if (hiddenInputRef.current) {
          hiddenInputRef.current.focus();
        }
        lastTapRef.current = null;
      } else {
        lastTapRef.current = now;
      }
    };

    const handleInputFocus = () => {
      setIsKeyboardOpen(true);
    };

    const handleInputBlur = () => {
      setIsKeyboardOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (rfbRef.current) {
        const key = e.key;
        const keysym = keySymMap[key] || key.charCodeAt(0);
        rfbRef.current.sendKey(keysym, key, true);
        e.preventDefault();
      }
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (rfbRef.current) {
        const key = e.key;
        const keysym = keySymMap[key] || key.charCodeAt(0);
        rfbRef.current.sendKey(keysym, key, false);
        e.preventDefault();
      }
    };

    return (
      <div
        ref={containerRef}
        className={clsx(
          "relative w-full h-full overflow-hidden rounded-2xl border-none",
          className
        )}
        style={style}
        onPointerDownCapture={() => {
          onBringToFront?.();
        }}
      >
        {noVNCContainerStore.isConnected && showDrawer && (
          <Drawer.Root
            open={noVNCContainerStore.isDrawerOpen}
            onOpenChange={noVNCContainerStore.toggleDrawer}
            direction="left"
          >
            <Drawer.Trigger asChild>
              <motion.div
                data-testid="quick-access-menu"
                className="absolute left-0 top-1/2 transform -translate-y-1/2 h-[80px] w-5 sm:w-4 z-50 cursor-pointer rounded-full rounded-l-lg flex items-center justify-center before:absolute before:inset-0 before:-z-10 before:bg-customGray/60 before:backdrop-filter before:backdrop-blur-[40px] before:backdrop-saturate-200 before:rounded-full before:rounded-l-lg"
              >
                <div className="w-1 h-12 rounded-full bg-white bg-opacity-80 backdrop-blur-sm"></div>
              </motion.div>
            </Drawer.Trigger>
            <Drawer.Overlay className="absolute inset-0 z-40 bg-black bg-opacity-50 rounded-2xl" />
            <Drawer.Content
              className={clsx(
                "bg-[radial-gradient(circle_at_26%_0,rgba(178,198,255,0.15),rgba(243,243,244,0.03)_33%,rgba(255,255,255,0)_56%,rgba(57,59,76,0.07))]",
                "before:absolute before:inset-0 before:-z-10 before:bg-customGray/30 before:backdrop-filter before:backdrop-blur-[40px] before:backdrop-saturate-200",
                "flex flex-col rounded-t-[10px] h-full w-[270px] absolute bottom-0 left-0 z-50"
              )}
            >
              <div className="p-4 flex-1 h-full">
                {/* TODO: Add drawer content component */}
              </div>
            </Drawer.Content>
          </Drawer.Root>
        )}

        <input
          ref={hiddenInputRef}
          type="text"
          style={{
            position: "absolute",
            opacity: 0,
            height: "1px",
            width: "1px",
            border: "none",
            outline: "none",
            zIndex: -1,
          }}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />

        {noVNCContainerStore.isReconnecting && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20">
            <Skeleton className="w-full h-full" />
          </div>
        )}

        {noVNCContainerStore.showReconnectUI && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20">
            <div className="text-center text-white">
              <p className="font-bold bg-gray-100/[0.1] text-white-500 px-4 py-1.5 rounded-md">
                Oops, you've lost the connection.
              </p>
              {noVNCContainerStore.remainingTime !== null &&
              noVNCContainerStore.remainingTime > 0 ? (
                <Button
                  onClick={handleReconnect}
                  className="mt-2 bg-white text-black"
                  isLoading={noVNCContainerStore.isReconnecting}
                  radius="md"
                >
                  Reconnect
                </Button>
              ) : (
                <p>Your session has expired.</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default NoVNCContainer;
